import React, { Fragment, useEffect, useRef } from 'react'
import { useState } from 'react'
import { Popover, Transition } from '@headlessui/react'
import { useTranslation } from 'app/i18n/client'
import cookieCutter from 'cookie-cutter'

import useQueryMembershipDetail from 'app/hooks/useQueryMembershipDetail'
import useQueryPointExpiringSummary from 'app/hooks/useQueryPointExpiringSummary'

import { Button, Loading, Logo, RankMedal } from 'app/components/atoms'
import { PointExpiryBanner } from 'app/components/molecules'

import ChangeLanguagePopup from './ChangeLanguagePopup'
import AccountInfo from './AccountInfo'
import MobileMenu from './MobileMenu'

import CrossIcon from 'public/icons/cross-lg.svg'
import MenuIcon from 'public/icons/menu.svg'
import LogoutModal from '../LogoutModal'
import { useParams } from 'next/navigation'
import useAuthentication from 'app/hooks/useAuthentication'
import { SIGN_UP_URL } from 'core/auth/constants'
import { toast } from 'react-toastify'

interface HeaderProps {
  isMobileMenuOpen?: boolean
  closeMobileMenu?: () => void
}

const Header: React.FunctionComponent<HeaderProps> = ({
  isMobileMenuOpen = false,
  closeMobileMenu,
}) => {
  const errMessageRef = useRef<string | null>(null)
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const [confirmLogout, setConfirmLogout] = useState(false)

  useEffect(() => {
    cookieCutter.set('NEXT_LOCALE', locale, {
      httpOnly: true,
      secure: true,
      sameSite: 'lax',
      path: '/',
    })
  }, [locale])

  const {
    isLoading: isLoadingProfile,
    data: profile,
    isError: isLoadingProfileError,
  } = useQueryMembershipDetail()

  const { isSuccess: isPointExpirySuccess, data: pointExpiry } =
    useQueryPointExpiringSummary()

  const {
    isAuthenticated,
    isLoading: isLoadingSession,
    login,
    logout,
    data: sessionData,
    resetErrorState,
  } = useAuthentication()

  const isLoadingCurrentUser = isLoadingProfile || isLoadingSession

  const onLogout = () => {
    logout()
    setConfirmLogout(false)
  }

  const handleCancelMembership = async () => {
    await logout({
      redirect: true,
      callbackUrl: `${process.env.NEXT_PUBLIC_SMART_CLUB_URL}/mypage/account/withdrawal`,
    })
  }

  const formatUsername = (
    locale: 'en' | 'ja' | 'ko' | 'zh-Hans' | 'zh-Hant',
    profile: { firstName?: string; lastName?: string },
  ) =>
    ({
      en: `${profile?.firstName} ${profile?.lastName}`,
      ja: `${profile?.lastName} ${profile?.firstName}`,
      ko: `${profile?.lastName} ${profile?.firstName}`,
      'zh-Hans': `${profile?.lastName} ${profile?.firstName}`,
      'zh-Hant': `${profile?.lastName} ${profile?.firstName}`,
    })[locale]

  const renderHonorific = (
    locale: 'en' | 'ja' | 'ko' | 'zh-Hans' | 'zh-Hant',
  ) =>
    ({
      en: '',
      ja: 'さん',
      ko: '님',
      'zh-Hans': '',
      'zh-Hant': '',
    })[locale]

  useEffect(() => {
    if (
      sessionData?.triplaSignInErrorMessage &&
      errMessageRef.current !== sessionData?.triplaSignInErrorMessage
    ) {
      errMessageRef.current = sessionData?.triplaSignInErrorMessage

      toast.error(t(sessionData?.triplaSignInErrorMessage))
      resetErrorState()
    }
  }, [t, sessionData?.triplaSignInErrorMessage, resetErrorState])

  return (
    <>
      <div className="bg-white h-full flex flex-col lg:block lg:bg-white lg:h-auto">
        <div className="lg:hidden">
          {!isMobileMenuOpen && (
            <PointExpiryBanner
              isAuthenticated={isAuthenticated}
              isPointExpirySuccess={isPointExpirySuccess}
              within60Days={pointExpiry?.within60Days}
            />
          )}
        </div>
        <div className="w-full lg:w-auto lg:max-w-7xl lg:mx-auto px-8 lg:px-0 flex items-center min-h-21 h-21 justify-between">
          <Logo />
          <div className="hidden lg:flex space-x-10 items-center justify-center">
            <ChangeLanguagePopup />
            {isLoadingCurrentUser ? (
              <div className="w-8 h-8 flex items-center justify-center">
                <Loading
                  className="!text-white !opacity-60"
                  size="sm"
                  baseClassName="!text-primary"
                />
              </div>
            ) : isAuthenticated && profile?.firstName && profile?.lastName ? (
              <AccountInfo
                userName={
                  profile
                    ? formatUsername(
                        locale as 'en' | 'ja' | 'ko' | 'zh-Hans' | 'zh-Hant',
                        profile,
                      )
                    : ''
                }
                honorific={
                  profile
                    ? renderHonorific(
                        locale as 'en' | 'ja' | 'ko' | 'zh-Hans' | 'zh-Hant',
                      )
                    : ''
                }
                rankNumber={profile?.rankNumber}
                rankName={profile?.rankName}
                pointInfo={{
                  isLoading: isLoadingProfile,
                  isError: isLoadingProfileError,
                  totalPoint: profile?.pointBalance,
                }}
                pointExpiry={{
                  isSuccess: isPointExpirySuccess,
                  within60Days: pointExpiry?.within60Days,
                }}
              />
            ) : (
              <div className="flex items-center">
                <Button
                  size="sm"
                  href={SIGN_UP_URL}
                  className="!px-3 !uppercase"
                >
                  {t('Sign Up')}
                </Button>
                <div className="px-4">{t('or')}</div>
                <Button
                  size="sm"
                  className="!px-3 !uppercase"
                  variant="white"
                  onClick={() => login({ prompt: 'login' })}
                >
                  {t('Log In')}
                </Button>
              </div>
            )}
          </div>
          <div className="w-full flex lg:hidden items-center justify-end gap-4">
            {isAuthenticated && (
              <RankMedal
                rankNumber={profile?.rankNumber}
                rankName={profile?.rankName}
                className="flex-none"
              />
            )}
            <Popover.Button className="flex-none bg-white h-12 flex flex-col justify-center items-end space-y-2 focus:outline-none">
              {isMobileMenuOpen ? (
                <CrossIcon className="fill-current text-gray-dark" />
              ) : (
                <MenuIcon className="fill-current text-primary" />
              )}
            </Popover.Button>
          </div>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Popover.Panel
            as="nav"
            className="w-full h-full lg:hidden overflow-y-auto p-8 pb-0"
            aria-label="Global"
          >
            <MobileMenu
              isAuthenticated={isAuthenticated}
              close={closeMobileMenu}
              onLogout={() => {
                closeMobileMenu && closeMobileMenu()
                setConfirmLogout(true)
              }}
              onDeleteAccount={handleCancelMembership}
            />
          </Popover.Panel>
        </Transition>
      </div>
      <LogoutModal
        open={confirmLogout}
        onClose={() => {
          setConfirmLogout(false)
        }}
        onConfirm={onLogout}
      />
    </>
  )
}

export default Header
